import type { ConductorRowResponse, EarthingResponse } from "../services/earthing";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import { StatusIcon } from "./StatusIcon";
import { bannerTone } from "./statusTone";

// The words of a design check with this module's own NO_SOLUTION words: a computed failure of
// a conductor's area or of the arrangement's resistance (D11).
const STATUS_LABELS: Record<EarthingResponse["status"], string> = {
  DESIGN_CHECK_PASSED: "Design check passed",
  REVIEW_REQUIRED: "Engineering review required",
  NO_SOLUTION: "Earthing check not met",
};

const STATUS_TONE: Record<EarthingResponse["status"], "pass" | "fail" | "warning"> = {
  DESIGN_CHECK_PASSED: "pass",
  REVIEW_REQUIRED: "warning",
  NO_SOLUTION: "fail",
};

// Both ratios of the strip are demand / capacity (the follow-up 41 words from day one): the
// required area over the declared area, the arrangement's resistance over the target.
export const RATIO_WORDS = "demand / capacity";
export const RATIO_PASS = "≤ 1 passes";

export type RowKind = "checked" | "review";

/** How one conductor row fared: nothing owed is checked; a figure, a source or a link owed is a review. */
export function rowKind(row: ConductorRowResponse): RowKind {
  return row.status === "DESIGN_CHECK_PASSED" ? "checked" : "review";
}

/** The failed items named: "Earthing check not met: PE-C-01, electrode resistance" or the other statuses' words. */
export function statusHeading(result: EarthingResponse): string {
  if (result.status !== "NO_SOLUTION") {
    return STATUS_LABELS[result.status];
  }
  const failed = result.conductors.filter((row) => row.met === false).map((row) => row.code);
  if (result.resistance_met === false) {
    failed.push("electrode resistance");
  }
  return `${STATUS_LABELS.NO_SOLUTION}: ${failed.join(", ")}`;
}

/** The conductor row with the largest required / declared ratio, when any row was compared. */
export function worstConductor(result: EarthingResponse): ConductorRowResponse | null {
  let worst: ConductorRowResponse | null = null;
  for (const row of result.conductors) {
    if (row.ratio === null) {
      continue;
    }
    if (worst === null || worst.ratio === null || Number(row.ratio) > Number(worst.ratio)) {
      worst = row;
    }
  }
  return worst;
}

/** "PE-C-01: 2.624 mm² of 2.5 mm² · demand / capacity 1.049 (≤ 1 passes)"; an em dash when no row was compared. */
export function conductorText(result: EarthingResponse): string {
  const worst = worstConductor(result);
  if (worst === null || worst.required_area_mm2 === null || worst.declared_area_mm2 === null || worst.ratio === null) {
    return EMPTY_QUANTITY;
  }
  return `${worst.code}: ${formatQuantity(worst.required_area_mm2, "mm²").display} of ${formatQuantity(worst.declared_area_mm2, "mm²").display} · ${RATIO_WORDS} ${formatQuantity(worst.ratio).display} (${RATIO_PASS})`;
}

/** "10.35 Ω of 20 Ω target · demand / capacity 0.5177 (≤ 1 passes)"; the resistance alone without a target; an em dash when not formed. */
export function resistanceText(result: EarthingResponse): string {
  if (result.arrangement_resistance_ohm === null) {
    return EMPTY_QUANTITY;
  }
  const resistance = formatQuantity(result.arrangement_resistance_ohm, "Ω").display;
  if (result.target_resistance_ohm === null || result.resistance_ratio === null) {
    return `${resistance} · no target declared`;
  }
  return `${resistance} of ${formatQuantity(result.target_resistance_ohm.value, "Ω").display} target · ${RATIO_WORDS} ${formatQuantity(result.resistance_ratio).display} (${RATIO_PASS})`;
}

type EarthingResultSummaryProps = {
  result: EarthingResponse;
};

// The decisive values first (Master Prompt v2.1 section 19): the outcome, the conductor rows
// and the worst of them, the electrodes and the arrangement against the target, the links.
// No figure is calculated here. Display rule A12: four significant figures on screen, the
// exact engine decimal in the tooltip (title attribute).
export function EarthingResultSummary({ result }: EarthingResultSummaryProps) {
  const tone = STATUS_TONE[result.status];
  const rows = result.conductors;
  const notMet = rows.filter((row) => row.met === false).length;
  const review = rows.filter((row) => rowKind(row) === "review").length;
  const linked = rows.filter((row) => row.disconnection_row !== null || row.linked_fault_run !== null).length;
  const electrodes = result.electrodes;
  const groups = electrodes.filter((item) => item.group_resistance_ohm !== null).length;
  const warningCount =
    result.warnings.length +
    rows.reduce((count, row) => count + row.warnings.length, 0) +
    electrodes.reduce((count, item) => count + item.warnings.length, 0);
  const worst = worstConductor(result);
  const resistivity = formatQuantity(result.design_resistivity_ohm_m, "Ω·m");

  return (
    <section aria-label="Result summary" data-summary-tone={tone}>
      <header>
        <p data-summary-status={result.status}>
          <StatusIcon tone={bannerTone(result.status, STATUS_TONE)} size={20} />
          {statusHeading(result)}
        </p>
        <p>
          Study <strong>{result.code}</strong> · {result.name} · bus {result.bus_code} · {result.jurisdiction_profile} profile
        </p>
      </header>
      <dl data-summary-strip>
        <div>
          <dt>Protective conductors</dt>
          {/* Counts of rows, not engineering quantities. */}
          <dd data-summary-field="conductors" data-summary-value="text">
            {rows.length} {rows.length === 1 ? "row" : "rows"} · {notMet} not met · {review} review · {linked} linked
          </dd>
        </div>
        <div>
          <dt>Worst conductor · required of declared</dt>
          <dd
            data-summary-field="conductor"
            title={
              worst === null || worst.required_area_mm2 === null
                ? undefined
                : `${worst.code}: ${worst.required_area_mm2} mm² · ${worst.declared_area_mm2 ?? "—"} mm² · ratio ${worst.ratio ?? "—"}`
            }
          >
            {conductorText(result)}
          </dd>
        </div>
        <div>
          <dt>Design resistivity</dt>
          <dd data-summary-field="resistivity" title={resistivity.exact ?? undefined}>
            {result.design_resistivity_ohm_m === null ? EMPTY_QUANTITY : resistivity.display}
          </dd>
        </div>
        <div>
          <dt>Electrodes</dt>
          {/* Counts of electrode rows and formed groups, not engineering quantities. */}
          <dd data-summary-field="electrodes" data-summary-value="text">
            {electrodes.length} {electrodes.length === 1 ? "row" : "rows"} · {groups} formed
          </dd>
        </div>
        <div>
          <dt>Arrangement · target</dt>
          <dd
            data-summary-field="resistance"
            title={
              result.arrangement_resistance_ohm === null
                ? undefined
                : `${result.arrangement_resistance_ohm} Ω${result.target_resistance_ohm === null ? "" : ` · ${result.target_resistance_ohm.value} Ω`}`
            }
          >
            {resistanceText(result)}
          </dd>
        </div>
        <div>
          <dt>Warnings</dt>
          <dd data-summary-warnings={warningCount}>{warningCount}</dd>
        </div>
      </dl>
    </section>
  );
}

export { STATUS_LABELS, STATUS_TONE };
export default EarthingResultSummary;
